import { DateTimeRangePicker } from "./datetime-range-picker.js";
import {
  COLOR_OPTIONS,
  FLAG_OPTIONS,
  META_STATUS_OPTIONS,
  TIER_OPTIONS,
  hasActiveFilters,
  type FilterState,
} from "./filter-state.js";
import type { UserMembership } from "../api/types.js";

// 1.D.6 + 2.E.3 Faceted filter -paneeli. Projektit, tier, meta-status,
// tagit, rating-vali, flag, color-label, kuvauspaivamaaran ikkuna.
// Jokainen muutos kutsuu onChange:a, joka paivittaa URL-paramit Selaa:ssa.

export type FilterPanelProps = {
  filters: FilterState;
  projects: UserMembership[];
  onChange: (next: FilterState) => void;
};

export function FilterPanel({ filters, projects, onChange }: FilterPanelProps) {
  function update<K extends keyof FilterState>(key: K, value: string) {
    const next: FilterState = { ...filters };
    if (value === "") delete next[key];
    else next[key] = value;
    onChange(next);
  }

  return (
    <div className="grid gap-3 rounded-md border border-slate-800 bg-slate-900/40 p-4 sm:grid-cols-2 md:grid-cols-4">
      <Field label="Projekti">
        <select
          value={filters.projectId ?? ""}
          onChange={(e) => update("projectId", e.target.value)}
          className={selectClass}
        >
          <option value="">Kaikki</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Tier">
        <select
          value={filters.tier ?? ""}
          onChange={(e) => update("tier", e.target.value)}
          className={selectClass}
        >
          <option value="">Kaikki</option>
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Meta-status">
        <select
          value={filters.metaStatus ?? ""}
          onChange={(e) => update("metaStatus", e.target.value)}
          className={selectClass}
        >
          <option value="">Kaikki</option>
          {META_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Tagit (pilkulla)">
        <input
          type="text"
          value={filters.tags ?? ""}
          placeholder="esim. wedding,2026"
          onChange={(e) => update("tags", e.target.value)}
          className={selectClass}
        />
      </Field>

      {/* 2.E.3 — uudet faceted-suodattimet */}
      <Field label="Rating min">
        <select
          value={filters.ratingMin ?? ""}
          onChange={(e) => update("ratingMin", e.target.value)}
          className={selectClass}
        >
          <option value="">—</option>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={String(n)}>
              {"★".repeat(n)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Flag">
        <select
          value={filters.flag ?? ""}
          onChange={(e) => update("flag", e.target.value)}
          className={selectClass}
        >
          <option value="">Kaikki</option>
          {FLAG_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Color label">
        <select
          value={filters.colorLabel ?? ""}
          onChange={(e) => update("colorLabel", e.target.value)}
          className={selectClass}
        >
          <option value="">Kaikki</option>
          {COLOR_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <div className="sm:col-span-2 md:col-span-4">
        <DateTimeRangePicker
          {...(filters.capturedFrom ? { from: filters.capturedFrom } : {})}
          {...(filters.capturedTo ? { to: filters.capturedTo } : {})}
          onChange={({ from, to }) => {
            const next: FilterState = { ...filters };
            if (from) next.capturedFrom = from;
            else delete next.capturedFrom;
            if (to) next.capturedTo = to;
            else delete next.capturedTo;
            onChange(next);
          }}
        />
      </div>

      {hasActiveFilters(filters) && (
        <div className="sm:col-span-2 md:col-span-4">
          <button
            type="button"
            onClick={() => onChange({})}
            className="text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
          >
            Tyhjenna filtterit
          </button>
        </div>
      )}
    </div>
  );
}

const selectClass =
  "w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-slate-400 focus:outline-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}
